import React, { useState, useEffect } from "react";
import { Button, Input, Form, DatePicker, Select, Row, Col, Typography } from "antd";
import axios from "axios";
import { Success } from "../../../commons/Messages/Messages";
import { isAuthenticated } from "../../../commons/Auth/auth";
import MainButtonComp from "../../Buttons/MainButtonComp";
import { generalErrorAlert } from "../../GeneralErrorAlert";
import GeneralModal from "../../../serviceProvider/components/General.modal";
import { useTranslation } from "react-i18next";
import { currencySymbol } from "../../../serviceProvider/components/accounting/shared/money";
import dayjs from "dayjs";
import { taxValue } from "../../../utils/taxValue";
import { useIsMobile } from "../../../design-system/useMediaQuery";
import CustomerBranchPicker from "../CustomerBranchPicker";
import CustomerSelect from "../CustomerSelect";
import { findBranch, sameId } from "../customerBranches";
import {
  OfferCatalogButton,
  OfferLineExtras,
  OfferMargin,
  OfferTextField
} from "../../../serviceProvider/components/materials/OfferCatalogTools";
import { offerTotals, titleHeaders } from "../../../serviceProvider/components/gaeb/calc";
import GaebItemInfo, { GaebHeadings } from "../../../serviceProvider/components/gaeb/GaebItemInfo";
import "./Offers.css";

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

// autoOpen: open the form for `data` right away without rendering the button (deep link
// /user/offers?open=<id>); onClose is called whenever the form closes.
const AddOffers = ({ updateParent, isUpdate = false, data, autoOpen = false, onClose }) => {
  const { t } = useTranslation("dashboard");
  const { t: ta } = useTranslation("accounting");
  const isMobile = useIsMobile();
  const auth = isAuthenticated();
  const serviceProviderId = auth?.employer?._id ? auth.employer._id : auth?._id;

  const [loading, setLoading] = useState(false);
  const [modalKey, setModalKey] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [items, setItems] = useState([
    { description: "", quantity: 1, unit: "Pauschale", price: 0, duration: "", total: 0 }
  ]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [clientsArray, setClientsArray] = useState([]);
  const [form] = Form.useForm();

  const [formData, setFormData] = useState({
    quotationNumber: "",
    title: "",
    client: null,
    date: dayjs().format("YYYY-MM-DD"),
    startDate: "",
    endDate: "",
    dueDate: "",
    tax: taxValue,
    currency: "EUR",
    description: "",
    objectName: "",
    objectAddress: ""
  });

  const getAllClients = async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/users/clients/limited`,
        { pageSize: 1000, pageNo: 0, serviceProvider: serviceProviderId },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );
      if (res?.status === 200) {
        setClientsArray(res.data?.data || []);
      }
    } catch (err) {
      console.error("Failed to load clients", err);
    }
  };

  useEffect(() => {
    getAllClients();
  }, []);

  // GAEB: unordered Bedarfs-/Alternativpositionen are priced but not added (gaeb/calc.js)
  const gaebTotals = offerTotals(items);
  const gaebHeadings = titleHeaders(items);
  const lvTitles = new Map((data?.gaeb?.boqStructure?.titles || []).map(x => [x.oz, x.label]));
  useEffect(() => {
    setTotalAmount(offerTotals(items).net / 100);
  }, [items]);

  const handleChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      [key]: value,
      // Another customer: its own branches
      ...(key === "client" && !sameId(prev.client, value)
        ? { clientBranch: null, billTo: "branch" }
        : {})
    }));
  };
  const selectedClient = clientsArray.find(c => sameId(c?._id, formData.client)) || null;

  const handleItemChange = (index, key, value) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [key]: value };
      if (key === "quantity" || key === "price") {
        newItems[index].total =
          parseFloat(newItems[index].price || 0) * parseFloat(newItems[index].quantity || 0);
      }
      return newItems;
    });
  };

  const addItem = () =>
    setItems(prev => [
      ...prev,
      { description: "", quantity: 1, unit: "Pauschale", price: 0, duration: "", total: 0 }
    ]);
  const removeItem = index => setItems(prev => prev.filter((_, i) => i !== index));
  // Material catalog: calculated lines replace an untouched empty first line
  const addCatalogItems = newItems =>
    setItems(prev => [...prev.filter(it => it.description || Number(it.price)), ...newItems]);
  const replaceItem = (index, next) =>
    setItems(prev => prev.map((it, i) => (i === index ? next : it)));

  const showModal = () => {
    if (isUpdate && data) {
      const startDate = data.startDate ? dayjs(data.startDate) : null;
      const endDate = data.endDate ? dayjs(data.endDate) : null;
      const dueDate = data.dueDate ? dayjs(data.dueDate) : null;
      const date = data.date ? dayjs(data.date) : dayjs();

      form.setFieldsValue({
        quotationNumber: data.quotationNumber,
        title: data.title,
        client: data.client?._id,
        date,
        startDate,
        endDate,
        dueDate,
        tax: data.tax ?? taxValue,
        currency: data.currency || "EUR",
        description: data.description,
        objectName: data.objectName,
        objectAddress: data.objectAddress
      });
      setItems(data.items || []);
      setFormData({
        ...data,
        client: data.client?._id,
        date: date.format("YYYY-MM-DD"),
        startDate: startDate ? startDate.format("YYYY-MM-DD") : "",
        endDate: endDate ? endDate.format("YYYY-MM-DD") : "",
        dueDate: dueDate ? dueDate.format("YYYY-MM-DD") : ""
      });
    } else {
      form.resetFields();
      setItems([
        { description: "", quantity: 1, unit: "Pauschale", price: 0, duration: "", total: 0 }
      ]);
      setFormData({
        quotationNumber: "",
        title: "",
        client: null,
        date: dayjs().format("YYYY-MM-DD"),
        startDate: "",
        endDate: "",
        dueDate: "",
        tax: taxValue,
        currency: "EUR",
        description: "",
        objectName: "",
        objectAddress: ""
      });
    }
    setModalKey(prev => prev + 1);
    setIsModalOpen(true);
  };

  useEffect(() => {
    if (autoOpen && data) showModal();
    // eslint-disable-next-line
  }, [autoOpen, data?._id]);

  const handleCancel = () => {
    setIsModalOpen(false);
    onClose?.();
    form.resetFields();
    setFormData({
      quotationNumber: "",
      title: "",
      client: null,
      date: dayjs().format("YYYY-MM-DD"),
      startDate: "",
      endDate: "",
      dueDate: "",
      tax: taxValue,
      currency: "EUR",
      description: "",
      objectName: "",
      objectAddress: ""
    });
    setItems([
      { description: "", quantity: 1, unit: "Pauschale", price: 0, duration: "", total: 0 }
    ]);
    setModalKey(prev => prev + 1);
  };

  const onFinish = async () => {
    setLoading(true);
    const values = form.getFieldsValue();

    const subTotal = totalAmount;
    const taxRate = parseFloat(values.tax || 0);
    const taxAmount = (subTotal * taxRate) / 100;
    const finalTotalAmount = subTotal + taxAmount;

    // Use formData for dates if they were changed via DatePicker,
    // otherwise use values if DatePicker was not interacted with but has initial value.
    const payload = {
      ...formData,
      ...values,
      date: formData.date,
      startDate: formData.startDate,
      endDate: formData.endDate,
      dueDate: formData.dueDate,
      items,
      clientBranch: formData.clientBranch || null,
      billTo: formData.billTo || "branch",
      subTotal,
      taxAmount,
      totalAmount: finalTotalAmount,
      serviceProvider: serviceProviderId,
      createdBy: auth?._id
    };

    try {
      let res;
      if (isUpdate) {
        res = await axios.put(
          `${process.env.REACT_APP_BACKEND_URL}/quotations/${data._id}`,
          payload,
          {
            headers: { Authorization: "Bearer " + localStorage.getItem("token") }
          }
        );
      } else {
        res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/quotations`, payload, {
          headers: { Authorization: "Bearer " + localStorage.getItem("token") }
        });
      }

      if (res.status === 200 || res.status === 201) {
        Success(t("Offer saved successfully"));
        setIsModalOpen(false);
        onClose?.();
        updateParent();
      }
    } catch (err) {
      generalErrorAlert(err);
    } finally {
      setLoading(false);
    }
  };

  const modalItems = (
    <Form key={modalKey} form={form} layout="vertical" onFinish={onFinish} className="offer-form">
      <Row gutter={[16, 0]}>
        <Col xs={24} md={12}>
          {/* AO-1: the gapless offer number is assigned when the offer is sent; the
              reference field stays optional for the tenant's own numbering. */}
          <Form.Item
            name="quotationNumber"
            label={t("Offer Number")}
            extra={
              isUpdate && data?.offerNumber
                ? `${ta("Offer number")}: ${data.offerNumber}`
                : ta("Leave empty: the number is assigned when the offer is sent")
            }
          >
            <Input
              size="large"
              placeholder={isUpdate && data?.offerNumber ? data.offerNumber : ta("Automatic")}
              onChange={e => handleChange("quotationNumber", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="title" label={t("Title")} rules={[{ required: true }]}>
            <Input
              size="large"
              placeholder={t("e.g. Renovation Offer")}
              onChange={e => handleChange("title", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item name="client" label={t("Client")} rules={[{ required: true }]}>
            <CustomerSelect
              size="large"
              placeholder={t("Select Client")}
              customers={clientsArray}
              branch={formData.clientBranch}
              onChange={(id, branchId, customer) =>
                setFormData(prev => ({
                  ...prev,
                  client: id,
                  clientBranch: branchId,
                  billTo: (branchId && findBranch(customer, branchId)?.defaultBillTo) || "branch"
                }))
              }
            />
          </Form.Item>
        </Col>
        {selectedClient?.branches?.length ? (
          <Col xs={24}>
            <CustomerBranchPicker
              customer={selectedClient}
              value={{ clientBranch: formData.clientBranch, billTo: formData.billTo }}
              onChange={sel => setFormData(prev => ({ ...prev, ...sel }))}
              showBranchSelect={false}
            />
          </Col>
        ) : null}
        <Col xs={24} md={12}>
          <Form.Item name="date" label={t("Date")} initialValue={dayjs()}>
            <DatePicker
              size="large"
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              onChange={date => handleChange("date", date ? date.format("YYYY-MM-DD") : "")}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="startDate" label={t("Start Date")}>
            <DatePicker
              size="large"
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              onChange={date => handleChange("startDate", date ? date.format("YYYY-MM-DD") : "")}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="endDate" label={t("End Date")}>
            <DatePicker
              size="large"
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              onChange={date => handleChange("endDate", date ? date.format("YYYY-MM-DD") : "")}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={8}>
          <Form.Item name="dueDate" label={t("Due Date")}>
            <DatePicker
              size="large"
              style={{ width: "100%" }}
              format="DD-MM-YYYY"
              onChange={date => handleChange("dueDate", date ? date.format("YYYY-MM-DD") : "")}
            />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item label={t("Tax")} name="tax">
            <Input size="large" onChange={e => handleChange("tax", e.target.value)} />
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item label={t("Currency")} name="currency">
            <Select size="large" onChange={value => handleChange("currency", value)}>
              <Option value="EUR">{t("EUR")} (€)</Option>
              <Option value="USD">{t("USD")} ($)</Option>
            </Select>
          </Form.Item>
        </Col>

        <Col xs={24} md={12}>
          <Form.Item label={t("Object Name")} name="objectName">
            <Input
              size="large"
              placeholder={t("Object Name")}
              onChange={e => handleChange("objectName", e.target.value)}
            />
          </Form.Item>
        </Col>
        <Col xs={24} md={12}>
          <Form.Item label={t("Object Address")} name="objectAddress">
            <Input
              size="large"
              placeholder={t("Object Address")}
              onChange={e => handleChange("objectAddress", e.target.value)}
            />
          </Form.Item>
        </Col>
      </Row>

      <OfferTextField
        kind="intro"
        value={formData.introText}
        onChange={value => handleChange("introText", value)}
      />

      <div className="offer-lines">
        <Title level={5}>{t("Items")}</Title>
        {items.map((item, index) => {
          const lineTotal = parseFloat(item.price || 0) * parseFloat(item.quantity || 0);
          const label = text =>
            isMobile ? <span className="offer-line__label">{text}</span> : null;
          return (
            <Row gutter={[8, 8]} key={index} align="bottom" className="offer-line">
              {item.gaeb ? (
                <Col xs={24}>
                  <GaebHeadings ozList={gaebHeadings[index]} titles={lvTitles} />
                  <GaebItemInfo
                    item={item}
                    counts={gaebTotals.positions[index]?.counts}
                    onChange={gaeb => handleItemChange(index, "gaeb", gaeb)}
                  />
                </Col>
              ) : null}
              <Col xs={24} md={8}>
                {label(t("Name"))}
                <Input
                  size="large"
                  placeholder={t("Name")}
                  value={item.description}
                  onChange={e => handleItemChange(index, "description", e.target.value)}
                />
              </Col>
              <Col xs={8} md={4}>
                {label(t("Quantity"))}
                <Input
                  size="large"
                  type="number"
                  inputMode="decimal"
                  placeholder={t("Amount")}
                  value={item.quantity}
                  onChange={e => handleItemChange(index, "quantity", e.target.value)}
                />
              </Col>
              <Col xs={8} md={4}>
                {label(t("Unit"))}
                <Select
                  size="large"
                  placeholder={t("Unit")}
                  style={{ width: "100%" }}
                  value={item.unit}
                  onChange={val => handleItemChange(index, "unit", val)}
                >
                  <Option value="Cm">{t("Cm")}</Option>
                  <Option value="M">M</Option>
                  <Option value="m²">m²</Option>
                  <Option value="KM">{t("KM")}</Option>
                  <Option value="std.">std.</Option>
                  <Option value="Minute">{t("Minute")}</Option>
                  <Option value="Hour">{t("Hour")}</Option>
                  <Option value="Pauschale">{t("Pauschale")}</Option>
                </Select>
              </Col>
              <Col xs={8} md={4}>
                {label(t("Price"))}
                <Input
                  size="large"
                  type="number"
                  inputMode="decimal"
                  placeholder={t("Price")}
                  value={item.price}
                  onChange={e => handleItemChange(index, "price", e.target.value)}
                />
              </Col>
              <Col xs={24} md={4}>
                {label(t("Duration"))}
                <Input
                  size="large"
                  placeholder={t("Duration")}
                  value={item.duration}
                  onChange={e => handleItemChange(index, "duration", e.target.value)}
                />
              </Col>
              <Col xs={24} className="offer-line__footer">
                <OfferLineExtras item={item} onChange={next => replaceItem(index, next)} />
                {isMobile && (
                  <span className="offer-line__total">
                    {t("Line Total")}:{" "}
                    <strong>
                      {lineTotal.toFixed(2)} {currencySymbol(formData.currency)}
                    </strong>
                  </span>
                )}
                <Button
                  type="link"
                  danger
                  className="offer-line__remove"
                  onClick={() => removeItem(index)}
                >
                  {t("Remove")}
                </Button>
              </Col>
            </Row>
          );
        })}
        <Button type="dashed" size="large" onClick={addItem} block>
          + {t("Add Item")}
        </Button>
        <OfferCatalogButton onAdd={addCatalogItems} />
        <OfferMargin items={items} />
      </div>

      <Row justify="end" className="offer-summary">
        <Col xs={24} md={12}>
          <p>
            {t("Subtotal")}: {totalAmount.toFixed(2)} {currencySymbol(formData.currency)}
          </p>
          <p>
            {t("Tax")}: {form.getFieldValue("tax") || 0}%
          </p>
          <Title level={4}>
            {t("Total")}:{" "}
            {(
              totalAmount +
              totalAmount * (parseFloat(form.getFieldValue("tax") || 0) / 100)
            ).toFixed(2)}{" "}
            {currencySymbol(formData.currency)}
          </Title>
        </Col>
      </Row>

      <OfferTextField
        kind="closing"
        value={formData.closingText}
        onChange={value => handleChange("closingText", value)}
      />

      <Form.Item name="description" label={t("Notes")}>
        <TextArea
          rows={3}
          placeholder={t("Internal notes or terms...")}
          onChange={e => handleChange("description", e.target.value)}
        />
      </Form.Item>
    </Form>
  );

  return (
    <>
      {!autoOpen && (
        <MainButtonComp
          text={isUpdate ? t("Edit") : t("Add Offer")}
          addBtn={!isUpdate}
          editBtn={isUpdate}
          onClick={showModal}
        />
      )}
      <GeneralModal
        title={isUpdate ? t("Update Offer") : t("Add New Offer")}
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={() => form.submit()}
        confirmLoading={loading}
        items={modalItems}
        width={1000}
      />
    </>
  );
};

export default AddOffers;
